<script lang="ts" setup>
import { ref, watch, useSupabaseClient, useAuth, useAtlas, useDialog, useRouter, onMounted, useRoute, useState } from '#imports';
import { useBreakpoints } from '@vueuse/core';
const supabase = useSupabaseClient();
const { atlasMarkers, hightlightSpot, setIsMapView } = useAtlas();

const { openDialog } = useDialog()
const { currentUser } = useAuth();
const contentVisible = ref(true);

const breakpoints = useBreakpoints({
  mobile: 920,
});

const { headerStyle, setHeaderStyle } = useAuth();
onMounted(() => {
  setHeaderStyle('opaque');
});

const viewStyle = useState<'list' | 'map'>('atlasView', () => 'map');

onMounted(() => {
  const isMobile = breakpoints.smaller('mobile');
  if (isMobile.value) {
    viewStyle.value = 'list';
    setIsMapView(false);
  } else if (currentUser.value) {
    setIsMapView(true);
  }
});

watch(currentUser, () => {
  if (currentUser.value) {
    setIsMapView(true);
  }
}, {immediate: true})

const toggleListStyle = () => {
  viewStyle.value = viewStyle.value === 'list' ? 'map' : 'list';
  if (viewStyle.value === 'map') {
    setIsMapView(true);
  } else {
    setIsMapView(false);
    contentVisible.value = true;
  }
}

const route = useRoute();
watch(route, () => {
  contentVisible.value = true;
});

const mapComponent = ref<HTMLDivElement | null>(null);
watch(hightlightSpot, () => {
  if (mapComponent.value) {
    mapComponent.value.centerOnSpot(hightlightSpot.value.id)
  }
});

const router = useRouter();
const openSpot = (spot: Tables<'spots'>) => {
  router.push(`/atlas/spots/${spot.current_slug}`);
}
</script>

<template>
  <div class="atlas" v-if="currentUser">
    <div :class="['wrapper', `view-style--${viewStyle}`]">
      <MPSectionHeader
        :center-title="false"
        class="title"
        title="Atlas"
        summary="Your subscriptions, saved spots, maps and more."
      />
      <aside>
        <ul>
          <li>
            <NuxtLinkLocale to="/atlas">
              <i class="ri-map-pin-fill"/>
              <span>Spots</span>
            </NuxtLinkLocale>
          </li>
          <!-- <li>
            <NuxtLinkLocale to="/atlas">
              <i class="ri-sparkling-line"/>
              <span>Whats New</span>
            </NuxtLinkLocale>
          </li> -->
          <li>
            <NuxtLinkLocale to="/atlas/maps">
              <i class="ri-compass-3-line"/>
              <span>Maps</span>
            </NuxtLinkLocale>
          </li>
          <li>
            <NuxtLinkLocale to="/atlas/creators">
              <i class="ri-user-heart-line"/>
              <span>Creators</span>
            </NuxtLinkLocale>
          </li>
          <li>
            <a to="/atlas/creators" @click="toggleListStyle" class="toggle-view-style">
              <i :class="viewStyle === 'map' ? 'ri-list-view' : 'ri-map-2-line'"/>
              <span>View as {{ viewStyle === 'map' ? 'List' : 'Map' }}</span>
            </a>
          </li>
        </ul>
      </aside>
      <section class="page-view">
        <NuxtPage v-if="contentVisible" :view-style="viewStyle"/>
      </section>
      <MPButton
        class="close-content"
        icon-only
        hide-label
        version="secondary"
        rounded
        icon="ri-arrow-left-double-fill"
        label="close content"
        :class="{'collapsed': !contentVisible}"
        @click="contentVisible = !contentVisible"
      />
      <div class="map-view">
        <ClientOnly>
          <MPMap
            v-if="viewStyle === 'map'"
            ref="mapComponent"
            :locations="atlasMarkers"
            @marker-click="openSpot($event)"
          />
        </ClientOnly>
      </div>
    </div>
  </div>
  <div v-else class="atlas-page">
    <section class="masthead">
      <div class="container">
        <h1>Start building your personal <span>Atlas</span></h1>
        <p class="subtitle">Save your favorite spots, maps and creators. Access them anytime, anywhere.</p>
        <MPButton
          label="Start your atlas"
          version="success"
          size="large"
          rounded
          @click="openDialog('MPAuth', { startOn: 'signup' })"
        />
      </div>
      <div>
        <MPImg src="/images/map-tags-icons.png" />
      </div>
    </section>
    <section>
      <div>
        <MPSectionHeader
          :center-title="false"
          subtitle="Save what you've discovered"
          title="Save your favorite Spots and maps"
          summary="Discover new places, save your favorite spots, and explore the world with your personalized Atlas."
        />
      </div>
      <div>
        <MPImg src="/images/card-world-stack.png" />
      </div>
    </section>
    <section>
      <div>
        <MPSectionHeader
          :center-title="false"
          subtitle="Creator Curated"
          title="Discover our Creator Recommendations"
          summary="Get the insider's perspective on the best places to visit across the world"
        />
      </div>
      <div>
        <MPImg src="/images/card-circle-graphic.png" />
      </div>
    </section>
    <section>
      <div>
        <MPSectionHeader
          :center-title="false"
          subtitle="Take it on the road"
          title="Save & track down hidden Gems"
          summary="Access your saved spots and maps anytime, anywhere."
        />
      </div>
      <div>
        <MPImg src="/images/person-spots-onroad.png" />
      </div>
    </section>
    <section class="faq-section">
      <div>
        <MPSectionHeader
          :center-title="false"
          subtitle="FAQ"
          title="Frequently Asked Questions"
          summary="Find out how it works and answers to frequently asked questions."
        />
      </div>

      <MPAccordion
        :items="[
          {
            id: '1',
            header: 'What is Mappr?',
            content:
              'Mappr is a way to support your favorite creators and have access to their knowledge of the world.',
          },
          {
            id: '2',
            header: 'Can I Save My own Spots',
            content:
              'Currently you can only save spots that have been uploaded by Creators.',
          },
          {
            id: '3',
            header: 'How much does it cost',
            content:
              'The Atlas is free for all Users, and we plan to keep it that way.',
          },
        ]"
        class="faq-list"
      />
    </section>
  </div>
</template>

<style lang="postcss" scoped>
@import 'assets/css/typography.pcss';
@import 'assets/css/scrollbar.pcss';
@import 'assets/css/layouts.pcss';
@define-mixin panel {
  background-color: white;
  /* border-radius: 1rem; */
  box-shadow: var(--shadow-md);
}

@define-mixin wrapper {
  position: relative;
  display: flex;
  flex-grow: 1;
}

/*
Base Layout
*/
.atlas {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

aside {
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      a {
        display: block;
        padding: 0.5rem 0;
      }
      a.router-link-exact-active {
        padding: 0.5rem 0;
        background-color: var(--color-rose-50);
        color: var(--color-rose-500);
      }
      a:hover {
        background-color: var(--color-slate-100);
      }
      i {
        display: block;
        font-size: 2rem;
        text-align: center;
      }
      span {
        display: block;
        padding: 0 0.5rem;
        text-align: center;
        line-height: 1.2;
        @mixin typeface-small;
        color: var(--color-slate-600);
      }
      button {
        width: 100%;
        padding: 0.5rem 0 !important;
        display: flex;
        gap: 0.5rem;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem !important;
      }
      button:deep(i) {
        display: block;
        font-size: 1.75rem !important;
        text-align: center;
      }
      button:deep(label) {
        margin: 0 !important;
      }
    }
  }
}

.view-style--map {
  @mixin wrapper;

  .map-view {
    display: flex;
    position: relative;
    flex-grow: 1;
    min-width: 100%;
    min-height: 100%;
  }

  aside {
    position: absolute;
    top: 0;
    left: 0;
    width: 5rem;
    height: 100%;
    z-index: 1;
    @mixin panel;
    box-shadow: var(--shadow-xs);
  }

  .page-view {
    position: absolute;
    z-index: 1;
    top: 0.75rem;
    left: 5.75rem;
    @mixin panel;
    width: 20rem;
    height: fit-content;
    border-radius: 1rem;
    max-height: calc(100vh - 8.25rem);
    overflow-y: scroll;
    overflow-x: show;
    @mixin scrollbar-reset;
    @mixin screen md {
      width: 30rem;
    }
    @mixin screen lg {
      width: 30rem;
    }
    @mixin screen xxl {
      width: 35rem;
    }
  }
}

.title {
  display: none;
}

.view-style--list {
  @mixin wrapper;
  display: grid;
  align-items: start;
  grid-template-rows: min-content 1fr;
  @mixin screen md {
    grid-template-rows: min-content min-content 1fr;
  }

  .map-view {
    display: none;
  }

  .toggle-view-style {
    display: none;
    @mixin screen md {
      display: flex;
    }
  }

  .page-view {
    position: relative;
    @mixin layout-wrapper-wide;
  }

  .close-content {
    display: none;
  }

  .title {
    @mixin screen md {
      @mixin layout-wrapper-wide;
      display: block;
      padding: 1rem 0 1rem;
    }
  }

  aside {
    @mixin layout-wrapper-wide;
    border-bottom: 1px solid var(--color-slate-100);
    @mixin screen sm {
      /* margin-top: 5rem; */
    }
    ul {
      display: flex;
      gap: 0.5rem;
      margin: -0.5rem 0.5rem 0.5rem;
      @mixin screen sm {
        margin: 0 0 0.5rem;
      }
      a,
      a.selected {
        display: grid;
        grid-template-columns: 1.5rem 1fr;
        padding: 0rem 0.5rem 0 0.65rem !important;
        /* background-color: transparent !important; */
        border-radius: 1rem;
        align-items: center;
        justify-content: center;
        span {
          @mixin typeface-regular;
          padding: 0 0.25rem;
        }
        i {
          font-size: 1.5rem;
        }
      }
    }
  }
}

.close-content {
  position: absolute;
  top: 1rem;
  left: 27rem;
  z-index: 1;
  @mixin screen md {
    left: 37rem;
  }
  @mixin screen xxl {
    left: 42rem;
  }
}
.close-content.collapsed {
  left: 6.5rem;
  rotate: 180deg;
}

.atlas-page {
  @mixin layout-wrapper-wide;
  section:deep(h1) {
    max-width: 30rem;
    margin: 0 auto;
  }
  section {
    margin: 0 1rem;
    padding: 2rem 0;
    display: flex;
    flex-direction: column;
    flex-direction: column-reverse;
    gap: 2rem;
    align-items: center;
    img {
      width: 70%;
      max-width: 35rem;
      margin: 0 auto;
    }
    text-align: center;
  }
  .masthead {
    @mixin layout-wrapper-wide;
    margin: 0 1rem 4rem;
    align-items: left;
    text-align: left;
    img {
      display: none;
      max-width: 45rem;
    }
    h1 {
      @mixin typeface-title;
      margin: 0 0 1rem;
      padding-right:4vw;
      max-width: 100vw;
      span {
        color: var(--color-green-400);
      }
    }
    p {
      @mixin typeface-summary;
      color: var(--color-slate-500);
      max-width: 28rem;
      margin-bottom: 1.75rem;
      padding-right: 2rem;
    }
  }
  .faq-section {
    flex-direction: column;
    padding: 0 1rem;
  }
  @mixin screen lg {
    section {
      max-width: 80%;
      text-align: left;
      padding: 4rem 0;
      display: flex;
      flex-direction: row;
      gap: 4rem;
      margin: 0 auto;
      align-items: center;
      justify-content: space-between;

      img {
        width: 100%;
        min-width: 10rem;
        margin: 0 auto;
      }

      h1 {
        max-width: 45rem;
      }
    }
    section:nth-child(even) {
      flex-direction: row-reverse;
    }
    .masthead {
      @mixin layout-wrapper-wide;
      margin: 8rem 2rem 6rem;
      grid-template-columns: 1fr 1fr;
      padding-right: 0;
      img {
        display: block;
      }
      h1 {
        @mixin typeface-huge;
        max-width: 50rem;
      }
      p {
        max-width: 28rem;
      }
    }
  }
  .faq-section {
    text-align: center;
    margin: 0 auto 4rem;
    > * {
      max-width: 40rem;
    }
  }
  .faq-section:deep(h1) {
    max-width: 25rem;
    margin: 0 auto;
  }
}
</style>
